import React, { useContext } from "react";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import { periodeIsActive } from "../../../../../util/date";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { defaultPersonFirmaRelationColumns } from "../../../../../config/person";
import ButtonDownload from "../../../../ButtonDownload";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import { personFirmarelationerForm } from "../../../../../config/form/person/stamoplysninger/personFirmarelationerFormConfig";
import { PersonDto } from "../../../../../interfaces/schema";
import { PersonFirmaRelationField } from "../../../../../interfaces/personer";
import { extractCurrentPersonFirma } from "../../../../../util/person";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { UserQueryContext } from "../../../../../context/UserContext";
import { orderBy } from "../../../../../util/orderBy";

export default function FirmaRelationer({
  invalidateData,
  person,
  personFirmaRelationColumns,
  setPersonFirmaRelationActiveColumns,
}: {
  person: PersonDto;
  personFirmaRelationColumns: IColumn<PersonFirmaRelationField>[];
  setPersonFirmaRelationActiveColumns: React.Dispatch<
    React.SetStateAction<PersonFirmaRelationField[]>
  >;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "BasisWrite");
  const { personFirma } = person;
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personFirmaRelationerSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  if (personFirma?.length === 0) {
    return (
      <IngenRegistreringer
        editConfig={
          isAuth
            ? personFirmarelationerForm(person.id, personFirma ?? [])
            : undefined
        }
        invalidateData={invalidateData}
      />
    );
  }
  const currentFirma = extractCurrentPersonFirma(person);
  const firmaWithPrimaer = personFirma?.map((f) => {
    return {
      ...f,
      primaereFirma: currentFirma?.id === f.id,
    };
  });

  // event handlers
  function downloadHandler() {
    tableToXLSX(personFirmaRelationColumns, firmaWithPrimaer ?? [], {
      fileName: `firmarelationer-for-${person.kundeNr}`,
      additionalData: {
        Kundenr: person.kundeNr,
      },
    });
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <TableRoot>
        <ModalProvider>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(personFirmaRelationColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <ColumnEditorModal
            defaultColumns={defaultPersonFirmaRelationColumns}
            columns={personFirmaRelationColumns}
            setActiveColumns={setPersonFirmaRelationActiveColumns}
          />
        </ModalProvider>
        <TableBody>
          {orderBy(
            firmaWithPrimaer,
            sortBy.map((s) => s.field),
            sortBy.map((s) => s.direction),
          ).map((firma, i) => {
            return (
              <TableRow
                key={i}
                editConfig={
                  isAuth
                    ? personFirmarelationerForm(
                        person.id,
                        personFirma ?? [],
                        firma.id,
                      )
                    : undefined
                }
                invalidateData={invalidateData}
              >
                <td
                  className={`indicator ${
                    periodeIsActive(
                      new Date(firma.periode.fraDato),
                      new Date(firma.periode.tilDatoInklusiv),
                    )
                      ? "has-effect"
                      : ""
                  }`}
                >
                  <ActiveIcon />
                </td>
                <>
                  {personFirmaRelationColumns
                    .filter((col) => col.active)
                    .map((col) => {
                      return <TD key={col.field} col={col} rowData={firma} />;
                    })}
                </>
              </TableRow>
            );
          })}
        </TableBody>
      </TableRoot>
      <OpretNyButtonBlock
        editConfig={
          isAuth
            ? personFirmarelationerForm(person.id, personFirma ?? [])
            : undefined
        }
        invalidateData={invalidateData}
      />
    </>
  );
}

const defaultSortBy: SortBy<PersonFirmaRelationField | "primaereFirma">[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
  {
    direction: "desc",
    field: "primaereFirma",
  },
];
